// Enhanced Payment Service for JOBENG Multi-Scenario System
// Professional Stripe integration with UK focus

import { PAYMENT_SCENARIOS, PaymentUtils } from '../config/paymentScenarios';

class PaymentService {
  constructor() {
    this.baseUrl = 'https://api.jobeng.ai'; // Update with your backend URL
    this.paymentEndpoint = '/api/payments';
    this.mockMode = false;
  }

  // Create Payment Intent for one-time purchases (courses, books, services, certifications)
  async createOneTimePayment(scenarioId, paymentType, userId, amountInPence, currency, description) {
    try {
      // // console.log('PaymentService.createOneTimePayment called with:', { scenarioId, paymentType, userId, amountInPence, currency });
      
      const response = await fetch(`${this.baseUrl}${this.paymentEndpoint}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInPence,
          currency: currency.toLowerCase(),
          type: `one_time_${paymentType}`,
          scenarioId,
          userId,
          description,
          metadata: {
            app: 'JOBENG',
            version: '1.0.0',
            paymentType,
            scenarioId
          }
        }),
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error creating one-time payment:', error);
      
      // Fallback to mock if backend is unavailable
      if (this.isNetworkError(error)) {
        // // console.log('Backend unavailable, using mock response for one-time payment');
        this.mockMode = true;
        return await this.getMockResponse('createOneTimePayment');
      }
      
      throw new Error(`Payment setup failed: ${error.message}`);
    }
  }

  // Create Subscription (Professional £14 or Business £39)
  async createSubscription(scenarioId, userId, amountInPence, currency) {
    try {
      // // console.log('PaymentService.createSubscription called with:', { scenarioId, userId, amountInPence, currency });
      // // console.log('PAYMENT_SCENARIOS available:', PAYMENT_SCENARIOS?.length || 'undefined');
      // // console.log('Available scenario IDs:', PAYMENT_SCENARIOS?.map(s => s.id) || 'none');
      
      // Check if scenario exists in PAYMENT_SCENARIOS
      const scenario = PaymentUtils?.getScenario ? PaymentUtils.getScenario(scenarioId) : null;
      // // console.log('Found scenario:', scenario);
      
      if (!scenario) {
        console.error('Scenario not found for ID:', scenarioId);
        // // console.log('PaymentUtils.getScenario function:', typeof PaymentUtils?.getScenario);
        
        // Create a mock scenario to continue processing
        const mockScenario = {
          id: scenarioId,
          type: 'subscription',
          name: scenarioId === 'prof_sub_monthly' ? 'Professional Premium' : 'Business Premium',
          description: 'Subscription service',
          interval: 'month'
        };
        // // console.log('Using mock scenario:', mockScenario);
        
        // Use the mock scenario instead of failing
        return await this.processSubscriptionWithScenario(mockScenario, userId, amountInPence, currency);
      } else if (scenario.type !== 'subscription') {
        throw new Error(`Scenario ${scenarioId} is not a subscription type (found: ${scenario.type})`);
      }

      return await this.processSubscriptionWithScenario(scenario, userId, amountInPence, currency);
    } catch (error) {
      console.error('Error creating subscription:', error);
      
      // Fallback to mock if backend is unavailable
      if (this.isNetworkError(error)) {
        // // console.log('Backend unavailable, using mock response for subscription');
        this.mockMode = true;
        return await this.getMockResponse('createSubscription');
      }
      
      throw new Error(`Subscription setup failed: ${error.message}`);
    }
  }

  // Helper method to process subscription with scenario
  async processSubscriptionWithScenario(scenario, userId, amountInPence, currency) {
    // Use mock response if backend is not available
    if (this.mockMode) {
      // // console.log('Using mock response for createSubscription');
      return await this.getMockResponse('createSubscription');
    }

    const response = await fetch(`${this.baseUrl}${this.paymentEndpoint}/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: amountInPence,
        currency: currency.toLowerCase(),
        type: 'subscription',
        scenarioId: scenario.id,
        userId,
        subscriptionType: scenario.id === 'prof_sub_monthly' ? 'professional' : 'business',
        interval: scenario.interval || 'month',
        description: scenario.description || `${scenario.name} subscription`,
        metadata: {
          app: 'JOBENG',
          version: '1.0.0',
          subscriptionType: scenario.id === 'prof_sub_monthly' ? 'professional' : 'business',
          scenarioId: scenario.id
        }
      }),
    });

    return await this.handleResponse(response);
  }

  // Get payment history for user
  async getPaymentHistory(userId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.paymentEndpoint}/history/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      
      if (this.isNetworkError(error)) {
        return { success: true, payments: [], message: 'No payment history available (offline mode)' };
      }
      
      throw error;
    }
  }

  // Check subscription status
  async checkSubscriptionStatus(userId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.paymentEndpoint}/subscription-status/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      
      if (this.isNetworkError(error)) {
        return {
          userId,
          status: 'inactive',
          message: 'Unable to check subscription status (offline mode)'
        };
      }
      
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(userId, subscriptionId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.paymentEndpoint}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          subscriptionId
        }),
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      
      if (this.isNetworkError(error)) {
        return { success: false, message: 'Unable to cancel subscription (offline mode)' };
      }
      
      throw error;
    }
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage += `: ${errorData.message || errorData.error || 'Unknown error'}`;
      } catch (parseError) {
        // If response is not JSON (e.g., HTML error page), get text
        try {
          const errorText = await response.text();
          if (errorText.includes('<html>') || errorText.includes('<!DOCTYPE')) {
            errorMessage += ': Server returned HTML instead of JSON (possible routing issue)';
          } else {
            errorMessage += `: ${errorText}`;
          }
        } catch (textError) {
          errorMessage += ': Unable to parse error response';
        }
      }
      
      throw new Error(errorMessage);
    }

    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      throw new Error('Invalid JSON response from server');
    }
  }

  // Helper method to check if error is network-related
  isNetworkError(error) {
    return error.message.includes('fetch') || 
           error.message.includes('Network request failed') || 
           error.message.includes('Failed to fetch') ||
           error.message.includes('Backend server');
  }

  // Mock responses for offline/testing mode
  async getMockResponse(method) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    switch (method) {
      case 'createSubscription':
        return {
          success: true,
          clientSecret: 'pi_mock_client_secret_for_testing',
          subscriptionId: 'sub_mock_subscription_id',
          message: 'Mock subscription created successfully (backend unavailable)'
        };
        
      case 'createOneTimePayment':
        return {
          success: true,
          clientSecret: 'pi_mock_client_secret_for_one_time',
          paymentIntentId: 'pi_mock_payment_intent_id',
          message: 'Mock payment intent created successfully (backend unavailable)'
        };
        
      default:
        return {
          success: false,
          message: 'Mock response not implemented for this method'
        };
    }
  }
}

// Export singleton instance
export default new PaymentService();
